import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "utils/redux/store";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { request } from "utils/request";
import { SampleDataInfo, SubscriptionStatus } from "./types";
import { responseStatus } from "utils/types";
import { trackDirectReportModalOpened } from "utils/trackingFunctions";

// ------------------ State Type/Structure ------------------
export interface ModalsState {
  isCreateTeamModalOpen: boolean;
  createTeamModalPreSelectedDepartmentId: number | null;
  redirectUponTeamCreation: boolean; // It might be worth to create a whole separate slice just for modals, if we have more than one modal persisting across pages.
  isFromAdminConsole: boolean;
  createdTeamId: number | null;
  editDepartmentModalOpen: boolean;
  editingDepartmentId: number | null;
  openTeamCreationAfterDepartmentCreation?: boolean | number | null;
  isInviteTeamLeaderModalOpen: boolean;
  inviteTeamLeaderModalTeamId: number | null;
  inviteTeamLeaderModalDepartmentId: number | null;
  isNewDirectReportModalOpen: {
    userAccountIds: number[];
  } | null;
  sampleDataInfo: SampleDataInfo | null;
  getSampleDataInfoStatus: responseStatus;
  isSubscriptionModalOpen: boolean;
  subscriptionStatus: SubscriptionStatus | null;
}

// ------------------ InitialState ------------------
const initialState: ModalsState = {
  isCreateTeamModalOpen: false,
  createTeamModalPreSelectedDepartmentId: null,
  redirectUponTeamCreation: false,
  isFromAdminConsole: false,
  createdTeamId: null,
  editDepartmentModalOpen: false,
  editingDepartmentId: null,
  openTeamCreationAfterDepartmentCreation: false,
  isInviteTeamLeaderModalOpen: false,
  inviteTeamLeaderModalTeamId: null,
  inviteTeamLeaderModalDepartmentId: null,
  sampleDataInfo: null,
  isNewDirectReportModalOpen: null,
  getSampleDataInfoStatus: "idle",
  isSubscriptionModalOpen: false,
  subscriptionStatus: null,
};
export const getSampleDataInfo = createAsyncThunk(
  "getSampleDataInfo",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/sampleDataInfo`;
    return (await request(requestUrl, {
      method: "GET",
    })) as SampleDataInfo;
  },
  {
    condition: (_, { getState }) => {
      const {
        modals: { getSampleDataInfoStatus },
      } = getState() as RootState;
      if (getSampleDataInfoStatus !== "idle") {
        return false;
      }
    },
  }
);
export const fetchSubscriptionStatus = () =>
  request(`${REACT_APP_API_URL}/talentInsights/subscriptionStatus`, {
    method: "GET",
  }) as Promise<SubscriptionStatus>;

export const getSubscriptionInfo = createAsyncThunk(
  "getSubscriptionStatus",
  // pendingRequest lets the caller start the request early (in parallel with other requests) and only apply the result here
  async (
    params: { pendingRequest?: Promise<SubscriptionStatus> } | undefined,
    { dispatch }
  ) => {
    const response = await (params?.pendingRequest ??
      fetchSubscriptionStatus());
    dispatch(setSubscriptionStatus(response));
    const isAccountActive =
      (response.isSubscribed || response.hasValidFreeTrial) &&
      !response.isSuspended;

    if (!isAccountActive) {
      dispatch(setIsSubscriptionModalOpen(true));
    }

    return isAccountActive;
  }
);
export const onSuccessfulDepartmentCreation = createAsyncThunk(
  "modals/onSuccessfulDepartmentCreation",
  async (departmentId: number | undefined, { getState, dispatch }) => {
    const {
      modals: { openTeamCreationAfterDepartmentCreation },
    } = getState() as RootState;

    if (openTeamCreationAfterDepartmentCreation) {
      dispatch(closeEditDepartmentModal()); // Only closing the modal when we redirect to thr team creation modal, because we will be showing the success screen inside of the modal.
      dispatch(openCreateTeamModal({ preSelectedDepartmentId: departmentId }));
    }
  }
);

// ------------------ Beginning of Slice Definition ------------------
export const modalsSlice = createSlice({
  name: "modals",
  initialState,
  reducers: {
    openCreateTeamModal(
      state,
      {
        payload,
      }: PayloadAction<
        | {
            redirectUponTeamCreation?: boolean;
            preSelectedDepartmentId?: number | null;
            isFromAdminConsole?: boolean;
          }
        | undefined
      >
    ) {
      return {
        ...state,
        redirectUponTeamCreation: !!payload?.redirectUponTeamCreation,
        isCreateTeamModalOpen: true,
        createTeamModalPreSelectedDepartmentId:
          payload?.preSelectedDepartmentId || null,
        isFromAdminConsole: !!payload?.isFromAdminConsole,
      };
    },
    closeCreateTeamModal(state) {
      state.redirectUponTeamCreation = false;
      state.isCreateTeamModalOpen = false;
      state.createdTeamId = null;
      state.createTeamModalPreSelectedDepartmentId = null;
    },
    setCreatedTeamId(state, { payload }: PayloadAction<number | null>) {
      state.createdTeamId = payload;
    },
    openEditDepartmentModal(
      state,
      {
        payload,
      }: PayloadAction<
        | {
            departmentId?: number;
            openTeamCreationAfterDepartmentCreation?: boolean | number;
          }
        | undefined
      >
    ) {
      state.editDepartmentModalOpen = true;
      state.editingDepartmentId = payload?.departmentId || null;
      state.openTeamCreationAfterDepartmentCreation =
        payload?.openTeamCreationAfterDepartmentCreation;
    },
    closeEditDepartmentModal(state) {
      state.editDepartmentModalOpen = false;
      state.editingDepartmentId = null;
      state.openTeamCreationAfterDepartmentCreation = false;
    },
    openInviteTeamLeaderModal(
      state,
      {
        payload,
      }: PayloadAction<
        | {
            teamId?: number | null;
            departmentId?: number | null;
          }
        | undefined
      >
    ) {
      state.isInviteTeamLeaderModalOpen = true;
      state.inviteTeamLeaderModalTeamId = payload?.teamId || null;
      state.inviteTeamLeaderModalDepartmentId = payload?.departmentId || null;
    },
    closeInviteTeamLeaderModal(state) {
      state.isInviteTeamLeaderModalOpen = false;
      state.inviteTeamLeaderModalTeamId = null;
      state.inviteTeamLeaderModalDepartmentId = null;
    },
    setIsNewDirectReportModalOpen(
      state,
      {
        payload,
      }: PayloadAction<{
        userAccountIds: number[];
      } | null>
    ) {
      state.isNewDirectReportModalOpen = payload;
      if (payload) {
        trackDirectReportModalOpened();
      }
    },
    setSubscriptionStatus(
      state,
      { payload }: PayloadAction<SubscriptionStatus>
    ) {
      state.subscriptionStatus = payload;
    },
    setIsSubscriptionModalOpen(state, { payload }: PayloadAction<boolean>) {
      state.isSubscriptionModalOpen = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSampleDataInfo.pending, (state) => {
        state.getSampleDataInfoStatus = "loading";
      })
      .addCase(
        getSampleDataInfo.fulfilled,
        (state, action: PayloadAction<SampleDataInfo>) => {
          state.getSampleDataInfoStatus = "succeeded";
          state.sampleDataInfo = action.payload;
        }
      )
      .addCase(getSampleDataInfo.rejected, (state) => {
        state.getSampleDataInfoStatus = "failed";
      });
  },
});

// ------------------ Selectors ------------------
export const selectCreateTeamModalIsOpen = (state: RootState) =>
  state.modals.isCreateTeamModalOpen;
export const selectCreateTeamModalPreSelectedDepartmentId = (
  state: RootState
) => state.modals.createTeamModalPreSelectedDepartmentId;
export const selectRedirectAfterCreatingTeam = (state: RootState) =>
  state.modals.redirectUponTeamCreation;
export const selectCreatedTeamId = (state: RootState) =>
  state.modals.createdTeamId;
export const selectEditDepartmentModalIsOpen = (state: RootState) =>
  state.modals.editDepartmentModalOpen;
export const selectEditingDepartmentId = (state: RootState) =>
  state.modals.editingDepartmentId;
export const selectIsModalComingFromAdminConsole = (state: RootState) =>
  state.modals.isFromAdminConsole;
export const selectInviteTeamLeaderModalIsOpen = (state: RootState) =>
  state.modals.isInviteTeamLeaderModalOpen;
export const selectInviteTeamLeaderModalTeamId = (state: RootState) =>
  state.modals.inviteTeamLeaderModalTeamId;
export const selectInviteTeamLeaderModalDepartmentId = (state: RootState) =>
  state.modals.inviteTeamLeaderModalDepartmentId;
export const selectSampleDataInfo = (state: RootState) =>
  state.modals.sampleDataInfo;
export const selectGetSampleDataInfoStatus = (state: RootState) =>
  state.modals.getSampleDataInfoStatus;
export const selectIsSubscriptionModalOpen = (state: RootState) =>
  state.modals.isSubscriptionModalOpen;
export const selectSubscriptionStatus = (state: RootState) =>
  state.modals.subscriptionStatus;
export const selectIsNewDirectReportModalOpen = (state: RootState) =>
  state.modals.isNewDirectReportModalOpen;

export const {
  openCreateTeamModal,
  closeCreateTeamModal,
  setCreatedTeamId,
  closeEditDepartmentModal,
  openEditDepartmentModal,
  openInviteTeamLeaderModal,
  closeInviteTeamLeaderModal,
  setIsNewDirectReportModalOpen,
  setIsSubscriptionModalOpen,
  setSubscriptionStatus,
} = modalsSlice.actions;

export default modalsSlice.reducer;
